import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Flame, MapPin, Maximize2, Scale, TriangleAlert } from 'lucide-react';
import {
  Bar,
  CHART_COLORS,
  Card,
  DashboardRow,
  DataTable,
  FilterBar,
  IconButton,
  Switch,
  LevelDot,
  MetricCard,
  MetricRow,
  Pager,
  PanelTitle,
  SelectableChip,
  TextField,
  type DataTableColumn,
  RangePicker,
} from '../../components';
import { formatCount } from '../../lib/format/money';
import { useTableUrlState } from '../../lib/table/tableSearch';
import { LocalePicker } from '../locales/LocalePicker';
import { listParam, pickParam, stringParam } from '../profiles/people';
import type { LocaleActivityRow, LocaleSortKey, LocaleTypeFilter } from './api';
import { Balance, ofTarget } from './localeParts';
import { ActivityMap, plottable, type MapFocus } from './ActivityMap';
import { LocaleFinder } from './LocaleFinder';
import { rangeTitle, useAnalyticsWindow } from './parts';
import { useLocaleActivity } from './queries';

const SORTS: Array<[LocaleSortKey, string]> = [
  ['dishesSold', 'Dishes sold'],
  ['dishesListed', 'Dishes listed'],
  ['chefCount', 'Chefs'],
  ['eaterCount', 'Eaters'],
  ['name', 'Name'],
];
const SORT_KEYS = SORTS.map(([k]) => k);

/** Dish locales by default — postal-code locales only when asked for. */
const TYPES: Array<[LocaleTypeFilter, string]> = [
  ['dish', 'Dish Locale'],
  ['postalCode', 'Postal Code'],
];
const TYPE_KEYS = TYPES.map(([k]) => k);

const COLUMNS: DataTableColumn<LocaleActivityRow>[] = [
  {
    id: 'name',
    header: 'Locale',
    width: '1.8fr',
    sortKey: 'name',
    cell: (r) => (
      <span className="inline-flex min-w-0 items-center gap-[9px]">
        <LevelDot level={r.activityLevel} />
        <span className="min-w-0">
          <span className="block truncate font-semibold">{r.name}</span>
          <span className="block truncate text-xs text-muted">
            {[[r.city, r.stateProvince].filter(Boolean).join(', '), ofTarget(r)].filter(Boolean).join(' · ')}
          </span>
        </span>
      </span>
    ),
  },
  {
    id: 'chefs',
    header: 'Chefs',
    width: '72px',
    align: 'right',
    numeric: true,
    sortKey: 'chefCount',
    cell: (r) => formatCount(r.chefCount),
  },
  {
    id: 'eaters',
    header: 'Eaters',
    width: '76px',
    align: 'right',
    numeric: true,
    sortKey: 'eaterCount',
    cell: (r) => formatCount(r.eaterCount),
  },
  {
    id: 'listed',
    header: 'Listed',
    width: '72px',
    align: 'right',
    numeric: true,
    sortKey: 'dishesListed',
    cell: (r) => formatCount(r.dishesListed),
  },
  {
    id: 'sold',
    header: 'Sold',
    width: '72px',
    align: 'right',
    numeric: true,
    sortKey: 'dishesSold',
    cell: (r) => formatCount(r.dishesSold),
  },
  { id: 'balance', header: 'Balance', width: '196px', cell: (r) => <Balance r={r} /> },
];

/**
 * Community Activation (kit `LocaleActivationScreen`; FE plan §7). One read of
 * `/admin/dashboard/locales` feeds the KPIs, the table and the sold-by-locale
 * bars. Counts are the trailing seven days. Activity level and supply/demand
 * are the BE's grading against each locale's own settings — rendered, never
 * recomputed here.
 */
export function CommunityActivationScreen() {
  const table = useTableUrlState();
  const f = table.filters;
  const sortBy = pickParam(table.sort?.key, SORT_KEYS) ?? 'dishesSold';
  const order = table.sort?.dir ?? 'desc';
  const region = stringParam(f.region);
  const localeIds = listParam(f.localeIds);
  const type = pickParam(f.type, TYPE_KEYS) ?? 'dish';
  // Chefs, listed and sold are counted over this window (7d by default); eaters are all-time.
  const range = useAnalyticsWindow({ range: '7d' });
  const days = { startDate: range.window.startDate, endDate: range.window.endDate };

  const [regionText, setRegionText] = useState(region ?? '');
  useEffect(() => {
    const t = setTimeout(() => {
      if ((regionText.trim() || undefined) !== region) table.setFilter('region', regionText.trim() || undefined);
    }, 300);
    return () => clearTimeout(t);
  }, [regionText, region, table]);

  const list = useLocaleActivity({
    page: table.page,
    pageSize: table.pageSize,
    region,
    localeIds,
    type,
    sortBy,
    order,
    ...days,
  });
  const rows = list.data?.items ?? [];
  // KPIs and the bars read every matching locale (the BE max page), not just the table page.
  const all = useLocaleActivity({
    page: 1,
    pageSize: 100,
    region,
    localeIds,
    type,
    sortBy: 'dishesSold',
    order: 'desc',
    ...days,
  });
  // The map always shows EVERY locale (the BE max page), whatever the page filters are: it is where
  // locales get added to the filter, so it must not be narrowed by it.
  const mapQuery = useLocaleActivity({ page: 1, pageSize: 100, sortBy: 'dishesSold', order: 'desc', ...days });
  const allRows = all.data?.items ?? [];
  const maxSold = Math.max(1, ...allRows.map((r) => r.dishesSold));
  const partial = (all.data?.total ?? 0) > allRows.length;
  const count = (pred: (r: LocaleActivityRow) => boolean) =>
    all.data ? formatCount(allRows.filter(pred).length) : '…';

  return (
    <div className="flex flex-col gap-[18px]">
      <div>
        <PanelTitle eyebrow="Locale snapshot" actions={<RangePicker value={range.range} onChange={range.setRange} />}>
          Activity at a glance
        </PanelTitle>
        <MetricRow>
          <MetricCard
            label="Locales"
            value={list.data ? formatCount(list.data.total) : '…'}
            hint={
              region || localeIds.length
                ? 'matching the filters'
                : type === 'dish'
                  ? 'all dish locales'
                  : 'all postal codes'
            }
            accent="var(--admin-chart-2)"
            icon={<MapPin size={15} aria-hidden="true" />}
          />
          <MetricCard
            label="High activity"
            value={count((r) => r.activityLevel === 'high')}
            hint={partial ? 'of the top 100 by sales' : 'at or above target'}
            accent="var(--admin-chart-3)"
            icon={<Flame size={15} aria-hidden="true" />}
          />
          <MetricCard
            label="Low activity"
            value={count((r) => r.activityLevel === 'low')}
            hint={partial ? 'of the top 100 by sales' : 'needs intervention'}
            accent="var(--admin-chart-4)"
            icon={<TriangleAlert size={15} aria-hidden="true" />}
          />
          <MetricCard
            label="Short on chefs"
            value={count((r) => r.supplyDemandFlag === 'chef_shortage')}
            hint={partial ? 'of the top 100 by sales' : 'eaters ≫ chefs'}
            accent="var(--admin-chart-5)"
            icon={<Scale size={15} aria-hidden="true" />}
          />
        </MetricRow>
      </div>

      <DashboardRow cols="minmax(0,1fr) 375px" height="auto">
        <div className="flex min-h-0 w-full flex-col">
          <FilterBar>
            <TextField
              aria-label="Filter by region"
              surface="white"
              placeholder="Filter by region…"
              value={regionText}
              onChange={(e) => setRegionText(e.target.value)}
              containerClassName="w-[200px] [&_input]:py-2 [&_input]:text-sm"
            />
            <LocalePicker value={localeIds} onChange={(ids) => table.setFilter('localeIds', ids.join(','))} />
            {TYPES.map(([k, label]) => (
              <SelectableChip
                key={k}
                active={type === k}
                onClick={() => table.setFilter('type', k === 'dish' ? undefined : k)}
              >
                {label}
              </SelectableChip>
            ))}
          </FilterBar>
          <FilterBar>
            <span className="text-xs text-muted">Sort</span>
            {SORTS.map(([k, label]) => (
              <SelectableChip
                key={k}
                active={sortBy === k}
                onClick={() => table.setSort({ key: k, dir: k === 'name' ? 'asc' : 'desc' })}
              >
                {label}
              </SelectableChip>
            ))}
          </FilterBar>
          <DataTable
            label="Locales"
            minWidth={700}
            columns={COLUMNS}
            rows={rows}
            getRowId={(r) => r.localeId}
            loading={list.isPending}
            error={list.error}
            onRetry={() => void list.refetch()}
            sort={{ key: sortBy, dir: order }}
            onSortChange={(s) => table.setSort(s)}
            emptyLabel="No locales match those filters."
          />
          <Pager
            page={table.page}
            pageSize={table.pageSize}
            total={list.data?.total ?? 0}
            onPageChange={table.setPage}
            onPageSizeChange={table.setPageSize}
            noun="locales"
          />
        </div>

        <Card
          surface="panel"
          header={
            <div className="flex-1">
              <PanelTitle eyebrow="Supply / demand">Dishes sold by locale</PanelTitle>
            </div>
          }
        >
          {allRows.length === 0 ? (
            <div className="text-[13px] text-muted">{all.isPending ? 'Loading…' : 'No locales.'}</div>
          ) : (
            <div className="flex flex-col gap-[11px]">
              {allRows.map((r, i) => (
                <Bar
                  key={r.localeId}
                  label={r.name}
                  value={formatCount(r.dishesSold)}
                  pct={(r.dishesSold / maxSold) * 100}
                  color={CHART_COLORS[i % CHART_COLORS.length]}
                  labelWidth={118}
                />
              ))}
            </div>
          )}
        </Card>
      </DashboardRow>

      <DensityMapCard
        rows={mapQuery.data?.items ?? []}
        windowLabel={rangeTitle(range.range).toLowerCase()}
        filteredIds={localeIds}
        onAddToFilter={(id) => {
          if (!localeIds.includes(id)) table.setFilter('localeIds', [...localeIds, id].join(','));
        }}
      />
    </div>
  );
}

/** The content well's bottom padding (AdminShell `pb-12`) — the margin kept under the expanded map. */
const WELL_BOTTOM_PX = 48;

/** The shell's scrolling content well (AdminShell `<main>`). */
function scrollerOf(el: HTMLElement | null): HTMLElement | null {
  return el?.closest('main') ?? null;
}

/** Visible content height minus the bottom margin — the expanded card's height. */
function fullHeightFor(el: HTMLElement | null): number | null {
  const main = scrollerOf(el);
  return main ? Math.max(360, main.clientHeight - WELL_BOTTOM_PX) : null;
}

/**
 * Activity-density map card. The header's full-screen control grows the card
 * (header + map) to the visible content area minus the bottom margin and
 * scrolls it into view; the down caret returns it to its default height.
 */
function DensityMapCard({
  rows,
  windowLabel,
  filteredIds,
  onAddToFilter,
}: {
  rows: LocaleActivityRow[];
  /** The page's range, for the map card's "sold (…)" line. */
  windowLabel: string;
  filteredIds: string[];
  onAddToFilter: (localeId: string) => void;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [fullHeight, setFullHeight] = useState<number | null>(null);
  const [focus, setFocus] = useState<MapFocus | null>(null);
  // Bumped on every map selection (shape click or finder pick) to reset the finder.
  const [finderReset, setFinderReset] = useState(0);
  const [moveOnClick, setMoveOnClick] = useState(true);
  // The finder searches only locales the map can actually show.
  const findable = useMemo(() => plottable(rows).map((p) => p.row), [rows]);
  const expanded = fullHeight !== null;

  // Track the viewport while expanded, so a window resize keeps it filled.
  useEffect(() => {
    if (!expanded) return;
    const onResize = () => setFullHeight(fullHeightFor(cardRef.current));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [expanded]);

  // Once the card has its new height, bring it fully into view.
  useEffect(() => {
    if (fullHeight === null) return;
    const main = scrollerOf(cardRef.current);
    main?.scrollTo({ top: main.scrollHeight, behavior: 'smooth' });
  }, [fullHeight]);

  return (
    <div
      ref={cardRef}
      className="flex flex-col overflow-hidden rounded-app-card border border-panel-border bg-white shadow-card"
      style={expanded ? { height: fullHeight } : undefined}
    >
      {/* Above the map (z-10) so the finder's results can drop over it. */}
      <div className="relative z-10 flex items-center gap-4 px-5 pb-3 pt-4">
        <div className="shrink-0">
          <div className="mb-[5px] text-[11px] font-bold uppercase tracking-[.14em] text-muted">
            Geographic map view
          </div>
          <h3 className="m-0 font-heading text-[19px] font-bold tracking-[-.01em] text-brown-deep">Activity density</h3>
        </div>
        <div className="flex min-w-0 flex-1 justify-center">
          <LocaleFinder
            key={finderReset}
            rows={findable}
            onPick={(localeId) => setFocus((f) => ({ localeId, nonce: (f?.nonce ?? 0) + 1 }))}
          />
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className="text-xs text-muted" aria-hidden="true">
            Move when clicked
          </span>
          <Switch size="sm" checked={moveOnClick} onChange={setMoveOnClick} label="Move when clicked" />
        </div>
        <IconButton
          ariaLabel={expanded ? 'Return map to default size' : 'Expand map to full screen'}
          size={32}
          onClick={() => setFullHeight(expanded ? null : fullHeightFor(cardRef.current))}
        >
          {expanded ? <ChevronDown size={18} aria-hidden="true" /> : <Maximize2 size={16} aria-hidden="true" />}
        </IconButton>
      </div>
      <div className={expanded ? 'min-h-0 flex-1' : 'h-[340px]'} style={{ borderTop: '1px solid rgba(143,68,32,.1)' }}>
        <ActivityMap
          rows={rows}
          windowLabel={windowLabel}
          focus={focus}
          filteredIds={filteredIds}
          onAddToFilter={onAddToFilter}
          onSelect={() => setFinderReset((n) => n + 1)}
          moveOnClick={moveOnClick}
        />
      </div>
    </div>
  );
}
